import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { trigger, transition, style, animate } from '@angular/animations';

export interface FieldStatus {
  id: string;
  name: string;
  status: 'found' | 'reading' | 'queued';
  statusId: string;
}

export interface ExtractedLinkedInData {
  fullName?: string;
  location?: string;
  education?: string;
  languages?: string[];
  skills?: string[];
  experience?: Array<any>;
}

@Component({
  selector: 'app-linkdindetails-fetch',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule
  ],
  templateUrl: './linkdindetails-fetch.component.html',
  styleUrl: './linkdindetails-fetch.component.scss',
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(20px)' }))
      ])
    ]),
    trigger('overlayAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class LinkdindetailsFetchComponent implements OnInit, OnDestroy, OnChanges {

  @Input() fileName: string = 'LinkedIn Profile';
  @Input() isVisible: boolean = false;
  /** When false the countdown runs but does NOT auto-complete. Call completeNow() or showError() externally. */
  @Input() autoComplete: boolean = true;
  /** Custom field rows to display; falls back to default LinkedIn fields when not provided. */
  @Input() initialFields?: FieldStatus[];

  @Output() onComplete = new EventEmitter<ExtractedLinkedInData>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() visibilityChange = new EventEmitter<boolean>();

  secondsRemaining: number = 8;
  isExtracting: boolean = true;
  hasExtractionError: boolean = false;
  timerInterval: any = null;
  extractedData: ExtractedLinkedInData | null = null;

  fields: FieldStatus[] = [
    { id: 'field-0', name: 'Full Name',   status: 'found',   statusId: 'status-0' },
    { id: 'field-1', name: 'Education',   status: 'found',   statusId: 'status-1' },
    { id: 'field-2', name: 'Experience',  status: 'reading', statusId: 'status-2' },
    { id: 'field-3', name: 'Skills',      status: 'queued',  statusId: 'status-3' }
  ];

  ngOnInit(): void {
    if (this.isVisible) {
      this.startCountdown();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      const nowVisible = changes['isVisible'].currentValue;
      const wasVisible = changes['isVisible'].previousValue;

      if (nowVisible === true && wasVisible !== true) {
        // Modal just became visible — reset and start
        this.reset();
      } else if (nowVisible === false) {
        this.stopCountdown();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  public show(fileName?: string): void {
    if (fileName) {
      this.fileName = fileName;
    }
    this.isVisible = true;
    this.visibilityChange.emit(true);
    this.reset();
  }

  public hide(): void {
    this.isVisible = false;
    this.visibilityChange.emit(false);
    this.stopCountdown();
    this.onClose.emit();
  }

  private startCountdown(): void {
    this.stopCountdown(); // ensure no duplicate intervals
    this.timerInterval = setInterval(() => {
      if (this.secondsRemaining > 0) {
        this.secondsRemaining--;
      }

      if (this.secondsRemaining === 4) {
        this.setRowFound(2);
        this.setRowReading(3);
      }

      if (this.secondsRemaining <= 0) {
        if (this.autoComplete) {
          this.completeExtraction();
        } else {
          // Keep spinner running; waiting for external completeNow() call
          this.stopCountdown();
        }
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private setRowFound(index: number): void {
    if (this.fields[index]) {
      this.fields[index] = { ...this.fields[index], status: 'found' };
    }
  }

  private setRowReading(index: number): void {
    if (this.fields[index]) {
      this.fields[index] = { ...this.fields[index], status: 'reading' };
    }
  }

  private completeExtraction(): void {
    this.stopCountdown();
    this.setRowFound(3);
    this.isExtracting = false;

    this.extractedData = {
      fullName: 'John Doe',
      location: 'San Francisco, CA',
      education: 'Stanford University',
      languages: ['English', 'Spanish'],
      skills: ['JavaScript', 'Angular', 'TypeScript', 'Node.js', 'AWS'],
      experience: [{ title: 'Senior Developer', company: 'Tech Company', duration: '2 years' }]
    };

  }

  public reset(): void {
    this.stopCountdown();
    this.secondsRemaining = 8;
    this.isExtracting = true;
    this.hasExtractionError = false;
    this.extractedData = null;

    if (this.initialFields && this.initialFields.length > 0) {
      this.fields = this.initialFields.map(f => ({ ...f }));
    } else {
      this.fields = [
        { id: 'field-0', name: 'Full Name',  status: 'found',   statusId: 'status-0' },
        { id: 'field-1', name: 'Education',  status: 'found',   statusId: 'status-1' },
        { id: 'field-2', name: 'Experience', status: 'reading', statusId: 'status-2' },
        { id: 'field-3', name: 'Skills',     status: 'queued',  statusId: 'status-3' }
      ];
    }

    this.startCountdown();
  }

  /** Call this when the external API returns success — marks all fields found and shows Continue. */
  public completeNow(): void {
    this.stopCountdown();
    this.fields = this.fields.map(f => ({ ...f, status: 'found' as const }));
    this.isExtracting = false;
    this.hasExtractionError = false;
    this.extractedData = {};
  }

  /** Call this when the external API returns an error — closes the modal automatically. */
  public showError(): void {
    this.stopCountdown();
    this.hasExtractionError = true;
    this.isExtracting = false;
    // Auto-close after a brief delay so the user sees the state change
    setTimeout(() => this.hide(), 1500);
  }

  public continueWithData(): void {
    this.hide();
    if (this.extractedData) {
      this.onComplete.emit(this.extractedData);
    }
  }

  public closeModal(): void {
    this.hide();
    this.onCancel.emit();
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  getFieldNameClass(status: string): string {
    if (status === 'reading') return 'field-active';
    if (status === 'queued') return 'field-queued';
    return '';
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'found':   return '✓ found';
      case 'reading': return 'reading';
      case 'queued':  return 'queued';
      default:        return '';
    }
  }
}